import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { UploadForm } from "@/components/textbooks/UploadForm";
import { PdfViewer } from "@/components/textbooks/PdfViewer";
import { QuizLinkDialog } from "@/components/textbooks/QuizLinkDialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, X, BookOpenText, Eye, Trash2, Users, ClipboardCheck } from "lucide-react";

const Row = ({ b, classNameById, onView, onToggle, onDelete, onQuiz }) => (
  <tr className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`tb-row-${b.id}`}>
    <td className="px-4 py-3">
      <p className="font-medium text-slate-900">{b.title}</p>
      <p className="text-xs text-slate-500">
        {b.chapter_number != null ? `Ch ${b.chapter_number}${b.chapter_title ? ` · ${b.chapter_title}` : ""}` : b.chapter_title || b.original_filename}
      </p>
    </td>
    <td className="px-4 py-3 text-sm text-slate-600">{b.board}</td>
    <td className="px-4 py-3 text-sm text-slate-600">{b.grade_level ? `Class ${b.grade_level}` : "—"}</td>
    <td className="px-4 py-3 text-sm text-slate-600">{b.subject}</td>
    <td className="px-4 py-3 text-xs text-slate-500" data-testid={`tb-uploader-${b.id}`}>{b.uploaded_by_name}<span className="block text-slate-400 capitalize">{b.uploaded_by_role}</span></td>
    <td className="px-4 py-3 text-xs text-slate-500">
      {b.class_ids.length ? <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{b.class_ids.map((c) => classNameById[c] || "Group").join(", ")}</span> : "—"}
    </td>
    <td className="px-4 py-3"><Switch checked={b.allow_download} onCheckedChange={() => onToggle(b)} data-testid={`tb-download-toggle-${b.id}`} /></td>
    <td className="px-4 py-3 text-right whitespace-nowrap">
      <button onClick={() => onQuiz(b)} className="btn-ghost text-xs" data-testid={`tb-quizzes-${b.id}`}>
        <ClipboardCheck className="w-4 h-4" /> Quizzes{b.quizzes?.length ? ` (${b.quizzes.length})` : ""}
      </button>
      <button onClick={() => onView(b)} className="btn-ghost text-xs" data-testid={`tb-view-${b.id}`}><Eye className="w-4 h-4" /> View</button>
      <button onClick={() => onDelete(b)} className="p-2 text-slate-400 hover:text-rose-600" data-testid={`tb-delete-${b.id}`}><Trash2 className="w-4 h-4" /></button>
    </td>
  </tr>
);

export default function TextbookManager() {
  const [books, setBooks] = useState([]);
  const [meta, setMeta] = useState({ boards: [], grades: [] });
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [quizBook, setQuizBook] = useState(null);

  const load = () => api.get("/textbooks").then((r) => setBooks(r.data));
  useEffect(() => {
    load();
    api.get("/textbooks/meta").then((r) => setMeta(r.data));
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
    api.get("/subjects").then((r) => setSubjects(r.data)).catch(() => {});
  }, []);
  const classNameById = Object.fromEntries(classes.map((c) => [c.id, c.name]));

  const toggle = async (b) => {
    try {
      await api.patch(`/textbooks/${b.id}`, { allow_download: !b.allow_download });
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };
  const del = async (b) => {
    if (!window.confirm(`Delete "${b.title}"? Students will lose access.`)) return;
    await api.delete(`/textbooks/${b.id}`);
    toast.success("Textbook deleted");
    load();
  };

  return (
    <AppShell title="Textbooks" subtitle="Upload PDF textbooks and chapters for your students.">
      <div className="flex justify-end mb-4">
        <button onClick={() => setShowNew(!showNew)} className="btn-primary text-sm" data-testid="tb-new-toggle">
          {showNew ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {showNew ? "Close" : "Upload textbook"}
        </button>
      </div>
      {showNew && meta.boards.length > 0 && (
        <UploadForm meta={meta} classes={classes} subjects={subjects} onDone={() => { setShowNew(false); load(); }} />
      )}
      {books.length === 0 ? (
        <div className="qs-card p-12 text-center text-slate-500" data-testid="tb-empty">
          <BookOpenText className="w-10 h-10 mx-auto mb-3 text-slate-300" /> No textbooks uploaded yet.
        </div>
      ) : (
        <div className="qs-card overflow-x-auto" data-testid="tb-table">
          <table className="w-full text-left">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-3">Title</th><th className="px-4 py-3">Board</th><th className="px-4 py-3">Class</th><th className="px-4 py-3">Subject</th><th className="px-4 py-3">Uploaded by</th><th className="px-4 py-3">Groups</th><th className="px-4 py-3">Download</th><th /></tr>
            </thead>
            <tbody>
              {books.map((b) => <Row key={b.id} b={b} classNameById={classNameById} onView={setViewing} onToggle={toggle} onDelete={del} onQuiz={setQuizBook} />)}
            </tbody>
          </table>
        </div>
      )}
      <PdfViewer book={viewing} onClose={() => setViewing(null)} canDownload />
      <QuizLinkDialog book={quizBook} onClose={() => setQuizBook(null)} onSaved={load} />
    </AppShell>
  );
}
